'use client';

import React from 'react';
import { Solicitud } from '@/types/solicitud';
import { User } from '@/types/user';
import { Switch } from '@/components/ui/Switch';
import { MontoInput } from '@/components/ui/MontoInput';
import { useAuth } from '@/hooks/useAuth';

interface ClabesSectionProps {
  localSolicitud: Solicitud;
  setLocalSolicitud: React.Dispatch<React.SetStateAction<Solicitud | null>>;
  setHasChanges: (v: boolean) => void;
  toggleDateField: (field: keyof Solicitud, enabled: boolean) => Promise<void>;
  updatingField: string | null;
  asesores: User[];
  error?: string | null;
}

const formatDate = (v?: string) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return v;
  }
};

export function ClabesSection({
  localSolicitud,
  setLocalSolicitud,
  setHasChanges,
  toggleDateField,
  updatingField,
  asesores,
  error,
}: ClabesSectionProps) {
  const { isFieldAllowed } = useAuth();
  const canEditInheritedID = isFieldAllowed('inheritedID');
  const canSeeRegion = isFieldAllowed('regionNombre');
  const canEditClienteNombre = isFieldAllowed('clienteNombre');
  const canEditClienteApellidoPaterno = isFieldAllowed('clienteApellidoPaterno');
  const canEditClienteApellidoMaterno = isFieldAllowed('clienteApellidoMaterno');
  const canEditClienteNuevoRenovacion = isFieldAllowed('clienteNuevoRenovacion');
  const canEditClienteClabe = isFieldAllowed('clienteClabe');
  const canEditProducto = isFieldAllowed('producto');
  const canEditMontoSolicitado = isFieldAllowed('montoSolicitado');
  const canEditMontoAutorizado = isFieldAllowed('montoAutorizado');
  const canEditMontoTransferencia = isFieldAllowed('montoTransferencia');
  const canSeeDocumentos = isFieldAllowed('documentos');
  const canAddArchivos = isFieldAllowed('addArchivos');
  const canListaArchivos = isFieldAllowed('listaArchivos');
  const canEditFechaEntregaARiesgos = isFieldAllowed('fechaEntregaARiesgos');
  const canEditFechaValidacionClabe = isFieldAllowed('fechaValidacionClabe');
  const canSeeComentarios = isFieldAllowed('comentarios');

  return (
    <div className="space-y-2">
      {error && (
        <div className="p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}
      <table className="w-full border-collapse text-sm">
        <tbody>
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200 w-1/3">Asesor Final</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              {canEditInheritedID ? (
                <select
                  className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  value={localSolicitud.inheritedID || ''}
                  onChange={(e) => { setLocalSolicitud({ ...localSolicitud, inheritedID: e.target.value }); setHasChanges(true); }}
                >
                  <option value="">Seleccionar asesor...</option>
                  {asesores.map((asesor) => (
                    <option key={asesor.ownerID} value={asesor.ownerID}>{asesor.name || asesor.userName}</option>
                  ))}
                </select>
              ) : (
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={asesores.find(a => a.ownerID === localSolicitud.inheritedID)?.name || asesores.find(a => a.ownerID === localSolicitud.inheritedID)?.userName || localSolicitud.inheritedID || ''} readOnly />
              )}
            </td>
          </tr>
          {canSeeRegion && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Region</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.regionNombre || ''} readOnly />
            </td>
          </tr>
          )}
          {canEditProducto && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Producto</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.producto || ''} readOnly />
            </td>
          </tr>
          )}
          {canEditClienteNombre && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Nombre</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input
                type="text"
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.clienteNombre || ''}
                onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteNombre: e.target.value }); setHasChanges(true); }}
              />
            </td>
          </tr>
          )}
          {canEditClienteApellidoPaterno && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Apellido Paterno</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input
                type="text"
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.clienteApellidoPaterno || ''}
                onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteApellidoPaterno: e.target.value }); setHasChanges(true); }}
              />
            </td>
          </tr>
          )}
          {canEditClienteApellidoMaterno && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Apellido Materno</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input
                type="text"
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.clienteApellidoMaterno || ''}
                onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteApellidoMaterno: e.target.value }); setHasChanges(true); }}
              />
            </td>
          </tr>
          )}
          {canEditClienteNuevoRenovacion && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Nuevo/Renovación</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={localSolicitud.clienteNuevoRenovacion || ''}
                onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteNuevoRenovacion: e.target.value }); setHasChanges(true); }}
              >
                <option value="">Seleccionar...</option>
                <option value="Nuevo">Nuevo</option>
                <option value="Renovación">Renovación</option>
              </select>
            </td>
          </tr>
          )}
          {canEditClienteClabe && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Cuenta Clabe</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 font-mono"
                value={localSolicitud.clienteClabe || ''}
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/\D/g, '');
                  setLocalSolicitud({ ...localSolicitud, clienteClabe: onlyNumbers });
                  setHasChanges(true);
                }}
                placeholder="Solo números"
              />
            </td>
          </tr>
          )}
          {canEditMontoSolicitado && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Solicitado ($)</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <MontoInput
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.montoSolicitado}
                onChange={(n) => { setLocalSolicitud({ ...localSolicitud, montoSolicitado: n }); setHasChanges(true); }}
              />
            </td>
          </tr>
          )}
          {canEditMontoAutorizado && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Autorizado ($)</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <MontoInput
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.montoAutorizado}
                onChange={(n) => { setLocalSolicitud({ ...localSolicitud, montoAutorizado: n }); setHasChanges(true); }}
              />
            </td>
          </tr>
          )}
          {canEditMontoTransferencia && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Transferencia ($)</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <MontoInput
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.montoTransferencia}
                onChange={(n) => { setLocalSolicitud({ ...localSolicitud, montoTransferencia: n }); setHasChanges(true); }}
              />
            </td>
          </tr>
          )}
          {canEditFechaEntregaARiesgos && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Entrega a Riesgos</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={formatDate(localSolicitud.fechaEntregaARiesgos)} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch
                checked={!!localSolicitud.fechaEntregaARiesgos}
                onChange={(c) => toggleDateField('fechaEntregaARiesgos', c)}
                color="blue"
                disabled={updatingField === 'fechaEntregaARiesgos'}
              />
            </td>
          </tr>
          )}
          {canEditFechaValidacionClabe && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Validación Clabe</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={formatDate(localSolicitud.fechaValidacionClabe)} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch
                checked={!!localSolicitud.fechaValidacionClabe}
                onChange={(c) => toggleDateField('fechaValidacionClabe', c)}
                color="blue"
                disabled={updatingField === 'fechaValidacionClabe'}
              />
            </td>
          </tr>
          )}
          {canSeeDocumentos && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Link Documentos</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              {canAddArchivos ? (
                <input
                  type="text"
                  className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  value={localSolicitud.documentos || ''}
                  onChange={(e) => { setLocalSolicitud({ ...localSolicitud, documentos: e.target.value }); setHasChanges(true); }}
                  placeholder="https://drive.google.com/..."
                />
              ) : (
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.documentos || ''} readOnly />
              )}
            </td>
          </tr>
          )}
          {canListaArchivos && localSolicitud.documentos && (
            <tr>
              <td className="px-3 py-2 border border-gray-200"></td>
              <td className="px-3 py-2 border border-gray-200" colSpan={2}>
                <button
                  type="button"
                  className="px-4 py-1 border border-gray-400 rounded bg-gray-100 hover:bg-gray-200 text-sm"
                  onClick={() => window.open(localSolicitud.documentos!, '_blank')}
                >
                  Ver documentos
                </button>
              </td>
            </tr>
          )}
          {canSeeComentarios && (
          <>
          <tr>
            <td className="px-3 py-2 text-left font-medium text-gray-600 border border-gray-200" colSpan={3}>Comentarios</td>
          </tr>
          <tr>
            <td className="px-3 py-2 border border-gray-200" colSpan={3}>
              <textarea
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                rows={4}
                value={localSolicitud.comentarios || ''}
                onChange={(e) => { setLocalSolicitud({ ...localSolicitud, comentarios: e.target.value }); setHasChanges(true); }}
                placeholder="Escribe comentarios aquí..."
              />
            </td>
          </tr>
          </>
          )}
        </tbody>
      </table>
    </div>
  );
}
